import React from 'react';
import { ConnectedAccountsState } from '../../../molecules/whatsapp/connected-accounts-state';
import { Text } from 'src/modules/shared/components/atoms/text';
import type {
  IntegrationAddress,
  WhatsAppIntegration,
} from 'src/api/generated';
import { IntegrationConnectionStatus } from 'src/api/generated';
import { SiWhatsapp } from 'react-icons/si';
import { WhatsAppAccountCard } from '../whatsapp-account-card';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { DisconnectedIntegrationCard } from '../../../molecules/whatsapp/disconnected-integration-card';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import { classifyWhatsAppIntegrationOnboardingSafety } from 'src/modules/shared/utils/integrations/whatsapp-integration-onboarding-safety.util';
import { WhatsAppConnectionActions } from '../../../molecules/whatsapp/whatsapp-connection-actions';
import { getWhatsAppConnectionPresentation } from '../../../../utils/whatsapp-connection-presentation.util';

type GroupedIntegrations = Record<string, IntegrationAddress[]>;

const getIntegrationDisplayName = (
  integration: WhatsAppIntegration
): string => {
  return integration.providerId;
};

const groupPhonesByIntegration = (
  phones: IntegrationAddress[]
): GroupedIntegrations => {
  const groups = new Map<string, IntegrationAddress[]>();
  phones.forEach((phone) => {
    const { integrationProviderId } = phone;
    const group = groups.get(integrationProviderId);
    if (group) {
      group.push(phone);
    } else {
      groups.set(integrationProviderId, [phone]);
    }
  });
  return Object.fromEntries(groups);
};

/** Inputs for the tenant WhatsApp integration list presentation. */
export interface WhatsAppIntegrationsListContentProps {
  /** Phone-number addresses grouped under the tenant integrations. */
  phones: IntegrationAddress[];
  /** Whether phone-number addresses are loading. */
  phonesLoading: boolean;
  /** The phone-number loading error, if any. */
  phonesError: Error | null;
  /** Tenant WhatsApp integrations including billing provenance. */
  integrations: WhatsAppIntegration[];
  /** Whether integrations are loading. */
  integrationsLoading: boolean;
  /** The integrations loading error, if any. */
  integrationsError: Error | null;
  /** Whether an integration removal is in flight. */
  removing: boolean;
  /** Whether the current user may remove integrations. */
  canDeleteIntegration: boolean;
  /** Requests removal of an integration by provider ID. */
  onRemoveIntegration: (integrationProviderId: string) => void;
  /** Whether connected and disconnected section headings are visible. */
  showSectionTitles?: boolean;
  /** Whether the operator can retry saved setup. */
  canRecoverIntegration?: boolean;
  /** Whether a recovery request is in flight. */
  recovering?: boolean;
  /** Retries setup without launching Meta authorization. */
  onRecoverIntegration?: (integrationProviderId: string) => void;
}

/** Renders connected and disconnected WhatsApp accounts with deletion safety. */
export const WhatsAppIntegrationsListContent: React.FC<
  WhatsAppIntegrationsListContentProps
> = ({
  phones,
  phonesLoading,
  phonesError,
  integrations,
  integrationsLoading,
  integrationsError,
  removing,
  canDeleteIntegration,
  onRemoveIntegration,
  showSectionTitles = true,
  canRecoverIntegration = false,
  recovering = false,
  onRecoverIntegration,
}) => {
  const { t } = useI18n();

  if (
    phonesLoading ||
    integrationsLoading ||
    phonesError ||
    integrationsError
  ) {
    return (
      <ConnectedAccountsState
        loading={phonesLoading || integrationsLoading}
        error={phonesError ?? integrationsError}
        showSectionTitles={showSectionTitles}
      />
    );
  }

  const groupedIntegrations = groupPhonesByIntegration(phones);
  const integrationsByProviderId = new Map(
    integrations.map((integration) => [integration.providerId, integration])
  );
  // A stale phone query must not render an errored account as connected too.
  const integrationIds = Object.keys(groupedIntegrations).filter((id) => {
    const status = integrationsByProviderId.get(id)?.connectionStatus;
    return !status || status === IntegrationConnectionStatus.ACTIVE;
  });

  const disconnectedIntegrations = integrations.filter(
    (integration) =>
      (integration.connectionStatus ?? IntegrationConnectionStatus.ACTIVE) !==
      IntegrationConnectionStatus.ACTIVE
  );

  return (
    <div className="space-y-4">
      {showSectionTitles ? (
        <div className="flex items-center justify-between">
          <Text size="h6" weight="medium">
            {t('settings.integrations.whatsapp.accounts.connectedHeading', {
              count: integrationIds.length,
            })}
          </Text>
        </div>
      ) : null}

      <div className="space-y-4">
        {phones.length === 0 ? (
          <EmptyState
            icon={<SiWhatsapp className="text-primary-500 w-full h-full" />}
            title={t('settings.integrations.whatsapp.accounts.emptyTitle')}
            description={t(
              'settings.integrations.whatsapp.accounts.emptyDescription'
            )}
          />
        ) : null}

        {integrationIds.map((integrationProviderId) => {
          const integration = integrationsByProviderId.get(
            integrationProviderId
          );
          const onboardingSafety = integration
            ? classifyWhatsAppIntegrationOnboardingSafety(integration)
            : null;

          return (
            <WhatsAppAccountCard
              key={integrationProviderId}
              integrationProviderId={integrationProviderId}
              phones={groupedIntegrations[integrationProviderId]}
              actions={
                <WhatsAppConnectionActions
                  state={getWhatsAppConnectionPresentation(integration)}
                  canRecover={
                    canRecoverIntegration && Boolean(onRecoverIntegration)
                  }
                  recovering={recovering || removing}
                  onRecover={() =>
                    onRecoverIntegration?.(integrationProviderId)
                  }
                  managed={onboardingSafety?.isComosManaged ?? false}
                  canManage={canDeleteIntegration}
                />
              }
              removalBlocked={
                !canDeleteIntegration || !onboardingSafety?.canDelete
              }
              removing={removing}
              onRemove={() => {
                const confirmed = window.confirm(
                  t('settings.integrations.whatsapp.accounts.confirmRemoval')
                );
                if (!confirmed) return;
                onRemoveIntegration(integrationProviderId);
              }}
            />
          );
        })}
      </div>

      {disconnectedIntegrations.length > 0 ? (
        <div className="border-t pt-6 space-y-4">
          <div className="flex items-center justify-between">
            <Text size={showSectionTitles ? 'h6' : 'small'} weight="medium">
              {t(
                'settings.integrations.whatsapp.accounts.disconnectedHeading',
                {
                  count: disconnectedIntegrations.length,
                }
              )}
            </Text>
          </div>

          <div className="space-y-4">
            {disconnectedIntegrations.map((integration) => {
              const onboardingSafety =
                classifyWhatsAppIntegrationOnboardingSafety(integration);

              return (
                <DisconnectedIntegrationCard
                  key={integration.providerId}
                  integrationProviderId={integration.providerId}
                  displayName={getIntegrationDisplayName(integration)}
                  connectionStatus={
                    integration.connectionStatus ??
                    IntegrationConnectionStatus.ACTIVE
                  }
                  statusUpdatedAt={
                    integration.connectionHealth?.statusUpdatedAt
                  }
                  actions={
                    <WhatsAppConnectionActions
                      state={getWhatsAppConnectionPresentation(integration)}
                      canRecover={
                        canRecoverIntegration &&
                        Boolean(onRecoverIntegration) &&
                        integration.connectionHealth?.lastFailureCode !==
                          'WhatsAppOffboardingPending'
                      }
                      recovering={recovering || removing}
                      onRecover={() =>
                        onRecoverIntegration?.(integration.providerId)
                      }
                      managed={onboardingSafety.isComosManaged}
                      canManage={canDeleteIntegration}
                    />
                  }
                  removalBlocked={
                    !canDeleteIntegration || !onboardingSafety.canDelete
                  }
                  removing={removing}
                  onRemove={() => {
                    const confirmed = window.confirm(
                      t(
                        'settings.integrations.whatsapp.accounts.confirmDisconnectedRemoval'
                      )
                    );
                    if (!confirmed) return;
                    onRemoveIntegration(integration.providerId);
                  }}
                />
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};
